import Link from "next/link";
import { NoSymbolIcon } from "@heroicons/react/24/outline";
import { Play } from "lucide-react";

import IconTile from "@/components/ui/icon-tile";
import { cn } from "@/lib/utils";

/**
 * The one control that opens a practice module, in two sizes: `compact` is
 * the chip in the home page's "Jump into a session" row, `card` is the tile
 * in the Practice grid (design-guidelines §5). Both show the module's own
 * icon, its title and a blue play disc that fills on hover, so the same
 * click looks like the same control at two sizes instead of two controls.
 *
 * The whole surface is the link, so the disc is a label, not a button (§7).
 * Hover changes colour only; the one motion is a press. A `card` with
 * `disabled` renders muted and unlinked with `disabledReason` in its footer.
 *
 * Not for: a module tab that selects rather than opens (RubricModuleTabs),
 * or the persona page's card-less module rows.
 *
 * No entry in primitive-shapes.mjs: a bordered link with an icon is every
 * card link. The guard is `/same-action` (`--key list:module` shows one look).
 */
const SIZES = {
  compact: "inline-flex items-center gap-2.5 rounded-xl py-1.5 pl-3 pr-1.5 text-[13.5px] font-medium",
  card: "flex h-full flex-col gap-3 rounded-xl p-5",
};

// The press is the shared `press-dip` (app/globals.css), shallower for a card.
const BASE = "group press-dip border [--press-scale:0.985]";
const LIVE =
  "border-gray-200 bg-white text-gray-900 hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2";
const DISABLED = "border-gray-100 bg-gray-50 opacity-70";

function PlayDisc({ className }) {
  return (
    <IconTile
      as="span"
      size="sm"
      shape="round"
      tone="action"
      aria-hidden="true"
      className={cn(
        "transition-colors duration-150 group-hover:bg-action-600 group-hover:text-white",
        className,
      )}
    >
      <Play className="ml-px h-3.5 w-3.5 fill-current" strokeWidth={0} />
    </IconTile>
  );
}

export default function ModuleLaunch({
  href,
  title,
  description,
  icon: Icon,
  size = "card",
  disabled = false,
  disabledReason,
}) {
  const layout = SIZES[size] || SIZES.card;

  if (size === "compact") {
    return (
      <Link href={href} className={cn(BASE, LIVE, layout)}>
        <Icon className="h-[17px] w-[17px] shrink-0 text-gray-600" aria-hidden="true" />
        <span className="pr-1.5">{title}</span>
        <PlayDisc />
      </Link>
    );
  }

  const content = (
    <>
      <IconTile icon={Icon} size="lg" tone="neutral" />
      <h3 className="text-base font-semibold leading-snug tracking-tight text-gray-900">
        {title}
      </h3>
      <p className="line-clamp-3 min-h-[3lh] text-sm leading-relaxed text-gray-600">
        {description}
      </p>
      <div className="mt-auto flex items-center justify-between gap-3 pt-1 text-xs text-gray-500">
        {disabled && disabledReason ? (
          <span className="flex items-center gap-1.5">
            <NoSymbolIcon className="h-4 w-4" />
            {disabledReason}
          </span>
        ) : (
          <span />
        )}
        <PlayDisc className={disabled ? "opacity-50" : undefined} />
      </div>
    </>
  );

  if (disabled) {
    return <div className={cn(BASE, DISABLED, layout)}>{content}</div>;
  }
  return (
    <Link href={href} className={cn(BASE, LIVE, layout)}>
      {content}
    </Link>
  );
}
